'use client';

import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ElementType,
  type ReactNode,
  type Ref,
} from 'react';
import styles from './reveal.module.css';

const cx = (...names: Array<string | false | null | undefined>) =>
  names.filter(Boolean).join(' ');

type RevealProps = {
  /** Stagger index — drives the entrance delay via the `--i` custom property (capped at 8). */
  index?: number;
  /** Element/component to render as. Defaults to `div`. */
  as?: ElementType;
  className?: string;
  children?: ReactNode;
  style?: CSSProperties;
  /** Any additional attributes are forwarded to the rendered element. */
  [key: string]: unknown;
};

/**
 * Reveal — scroll-in reveal wrapper. Children fade + rise into view once the
 * element enters the viewport (via IntersectionObserver), staggered by `index`.
 * Falls back to immediately-shown when IntersectionObserver is unavailable, and
 * collapses to instant under `prefers-reduced-motion: reduce`.
 */
export function Reveal({
  index = 0,
  as: Tag = 'div',
  className,
  children,
  style,
  ...rest
}: RevealProps) {
  const ref = useRef<HTMLElement | null>(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || shown) return;
    if (typeof IntersectionObserver === 'undefined') {
      // No IO support — reveal on the next frame (deferred so we never call
      // setState synchronously inside the effect body).
      const raf = requestAnimationFrame(() => setShown(true));
      return () => cancelAnimationFrame(raf);
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setShown(true);
            io.disconnect();
          }
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -8% 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [shown]);

  const cappedIndex = Math.min(index, 8);
  const Component: ElementType = Tag;

  return (
    <Component
      ref={ref as Ref<HTMLElement>}
      className={cx(styles.reveal, shown && styles.isIn, className)}
      style={{ '--i': cappedIndex, ...style } as CSSProperties}
      {...rest}
    >
      {children}
    </Component>
  );
}
